import { filter } from "lodash";
import { useState, useEffect } from "react";
// material
import {
  Card,
  Table,
  TableRow,
  TableBody,
  TableCell,
  Container,
  TableContainer,
  Stack,
  Typography,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
// components
import Page from "../components/Page";
import Scrollbar from "../components/Scrollbar";
import SearchNotFound from "../components/SearchNotFound";
import {
  TableTitle,
  TableHeader,
} from "../components/_dashboard/user";
import { db } from "../database/db";
import { collection, onSnapshot, addDoc, serverTimestamp, query, where, getDocs, orderBy as fsOrderBy } from "firebase/firestore";
import { Button } from "@mui/material";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_user) => _user.symbol.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function TopFIV() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("desc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("fivShares");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [stockList, setStockList] = useState([]);
  const [cryptoList, setCryptoList] = useState([]);
  const [tabValue, setTabValue] = useState(0);

  const TABLE_HEAD = [
    { id: "symbol", label: "Symbol", alignRight: false },
    { id: "fivShares", label: "FIV Shares", alignRight: false },
    { id: "marketShares", label: "Market Shares", alignRight: false },
    { id: "squeeze", label: "Squeeze %", alignRight: false },
    { id: "status", label: "Status", alignRight: false },
  ];

  const archiveToLegal = async (item) => {
    try {
      // Check if we already archived this specific symbol with this specific squeeze value today
      // to avoid spamming the archive
      const archiveRef = collection(db, "LegalArchive");
      const q = query(archiveRef, 
        where("symbol", "==", item.symbol), 
        where("fivShares", "==", item.fivShares)
      );
      const existing = await getDocs(q);
      
      if (existing.empty) {
        await addDoc(archiveRef, {
          symbol: item.symbol,
          fivShares: item.fivShares,
          marketShares: item.marketShares,
          squeeze: item.squeeze,
          timestamp: serverTimestamp(),
          reason: "Squeeze Exceeded 100%"
        });
        console.log(`Archived ${item.symbol} to Legal Records`);
      }
    } catch (e) {
      console.error("Legal Archive Error:", e);
    }
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "SqueezeData"), (snapshot) => {
      const stocks = [];
      const cryptos = [];
      
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const fiv = data.fivShares || 0;
        const market = data.marketShares || 1; // avoid div by zero
        const squeeze = (fiv / market) * 100;
        
        const item = {
          id: docSnap.id,
          ...data,
          squeeze: squeeze
        };

        // LEGAL ARCHIVE LOGIC: Auto-save if > 100%
        if (squeeze > 100) {
          archiveToLegal(item);
        }

        // Determine if stock or crypto based on symbol or a 'type' field if available
        // For now, assuming common crypto symbols or explicit type
        if (data.type === 'crypto' || ['BTC', 'ETH', 'SOL'].includes(data.symbol)) {
          cryptos.push(item);
        } else {
          stocks.push(item);
        }
      });
      
      setStockList(stocks);
      setCryptoList(cryptos);
    });

    return () => unsubscribe();
  }, []);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const currentList = tabValue === 0 ? stockList : cryptoList;
  
  const filteredList = applySortFilter(
    currentList,
    getComparator(order, orderBy),
    ""
  );

  const TableCellStyles = styled((props) => <TableCell {...props} />)(
    ({ theme }) => ({
      border: "1px solid #e3e6f0",
      fontSize: "12px",
    })
  );

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const exportToCSV = async () => {
    const q = query(collection(db, "LegalArchive"), fsOrderBy("timestamp", "desc"));
    const snapshot = await getDocs(q);
    let csvContent = "data:text/csv;charset=utf-8,Symbol,FIV Shares,Market Shares,Squeeze %,Timestamp\n";
    
    snapshot.forEach(doc => {
      const d = doc.data();
      csvContent += `${d.symbol},${d.fivShares},${d.marketShares},${d.squeeze}%,${d.timestamp?.toDate().toISOString()}\n`;
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "legal_fiv_archive.csv");
    document.body.appendChild(link);
    link.click();
  };

  return (
    <Page title="Top FIV Meters">
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4" gutterBottom>
            Top FIV Meters
          </Typography>
          <Button variant="contained" color="warning" onClick={exportToCSV}>
            Export Legal Records (CSV)
          </Button>
        </Stack>

        <Card sx={{ borderRadius: "5px" }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="FIV tabs">
              <Tab label="Stocks" />
              <Tab label="Crypto" />
            </Tabs>
          </Box>
          
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, p: "18px" }}>
              <Table>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={currentList.length}
                  numSelected={0}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {filteredList
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => {
                      const { id, symbol, fivShares, marketShares, squeeze } = row;
                      const isOver = squeeze > 100;

                      return (
                        <TableRow hover key={id} tabIndex={-1} sx={{ backgroundColor: isOver ? 'rgba(255, 0, 0, 0.1)' : 'inherit' }}>
                          <TableCellStyles align="left">{symbol}</TableCellStyles>
                          <TableCellStyles align="left">{fivShares?.toLocaleString()}</TableCellStyles>
                          <TableCellStyles align="left">{marketShares?.toLocaleString()}</TableCellStyles>
                          <TableCellStyles align="left" sx={{ color: isOver ? 'error.main' : 'success.main', fontWeight: 'bold' }}>
                            {squeeze?.toFixed(2)}%
                          </TableCellStyles>
                          <TableCellStyles align="left">
                            {isOver ? (
                              <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                                ARCHIVED (Legal)
                              </Typography>
                            ) : "Normal"}
                          </TableCellStyles>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
        </Card>
      </Container>
    </Page>
  );
}
